import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { Subject } from 'rxjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ErrorMessages, Metrics } from '@app/shared/constants/constants';
import { ToastTitle } from '@app/shared/constants/global.enum';
import {
  PEOPLE_METRICS,
  ChartHelper,
  SkillDistribution,
  MetricsResponse,
  SkillsResponse,
  MetricItem,
  QuarterOptions,
} from '@app/shared/constants/people-metrics.constants';
import {
  peopleMetricsLabels,
  PersonaType,
} from '@app/shared/constants/global.enum';
import { PeopleMetricsService } from './people-metrics.service';
import { ToasterService } from '@app/shared/services/toaster.service';
import { AuthService } from '@core/auth.service';
import { generateQuarterOptions, hasLevel2Or3Access } from '@utils/utils';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataObject } from '@app/shared/interfaces/common.model';

@UntilDestroy()
@Component({
  selector: 'app-people-metrics',
  templateUrl: './people-metrics.component.html',
  styleUrls: ['./people-metrics.component.scss'],
})
export class PeopleMetricsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  errorMsg: string = ErrorMessages.NoDataAvailable;
  imageSrc: string = PEOPLE_METRICS.IMAGES.NO_DATA;
  errorMessage: string | null = null;
  chartData: any = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }],
  };
  chartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        formatter: (value: any, context: any) => {
          const label = context.chart.data.labels[context.dataIndex];
          const properCaseLabel = ChartHelper.toProperCase(label || '');
          const wrappedSkillName = ChartHelper.wrapText(
            properCaseLabel,
            PEOPLE_METRICS.CHART.WRAP_TEXT_LENGTH,
          );
          return [value.toString(), ...wrappedSkillName];
        },
        color:
          getComputedStyle(document.documentElement)
            .getPropertyValue('light-black')
            .trim() || '#010101',
        align: 'end',
        anchor: 'end',
        offset: 15,
        textAlign: 'center',
        font: {
          size: 12,
          style: 'normal',
        },
        padding: 8,
        borderRadius: 6,
        clip: false,
        display: true,
      },
    },
    layout: {
      padding: {
        top: 60,
        bottom: 60,
        left: 60,
        right: 60,
      },
    },
    cutout: '60%',
    radius: '95%',
  };
  chartPlugins = [ChartDataLabels];
  isDataLoaded: boolean = false;
  isLoading: boolean = false;
  metrics: MetricItem[] = [];
  peopleMetricsLabels = peopleMetricsLabels;
  Metrics = Metrics;

  // For skills section
  selectedSkillsMonth: number = 1;
  skillsMonth: QuarterOptions[] = [];
  skillsYear: number = new Date().getFullYear();

  // For metrics section
  selectedMetricsMonth: number = 1;
  metricsMonth: QuarterOptions[] = [];
  metricsYear: number = new Date().getFullYear();
  private employeeId: string = '';
  @Input() vendorId: string | null = null;

  hasLevel2Or3Access: boolean = false;
  personaType = PersonaType;
  @Input() fieldVisibility: string = '';
  orgLevelMessage: string = PEOPLE_METRICS.ORG_LEVEL_MESSAGE;

  constructor(
    private peopleMetricsService: PeopleMetricsService,
    private toasterService: ToasterService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.checkUserLevel();
    this.initializeIds();
    const currentYear = new Date().getFullYear();
    this.skillsMonth = generateQuarterOptions(currentYear, 1);
    this.metricsMonth = generateQuarterOptions(currentYear, 1);
    this.updateChartOptionsForDevice();

    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
    const currentMonthLabel = `Q${currentQuarter} ${now.getFullYear()}`;

    const currentSkillsMonth = this.skillsMonth.find(
      (option) => option.label === currentMonthLabel,
    );
    if (currentSkillsMonth) {
      this.selectedSkillsMonth = currentSkillsMonth.value;
      this.skillsYear = now.getFullYear();
      this.loadSkillsData();
    }

    if (this.hasLevel2Or3Access) {
      const currentMetricsMonth = this.metricsMonth.find(
        (option) => option.label === currentMonthLabel,
      );
      if (currentMetricsMonth) {
        this.selectedMetricsMonth = currentMetricsMonth.value;
        this.metricsYear = now.getFullYear();
        this.loadMetricsData();
      }
    }

    window.addEventListener('resize', () => {
      this.updateChartOptionsForDevice();
    });
    this.cdr.detectChanges();
  }

  private checkUserLevel(): void {
    const userProfile = this.authService.userProfile;
    this.hasLevel2Or3Access =
      this.fieldVisibility === this.personaType.OpsPersona ||
      hasLevel2Or3Access(userProfile?.employeeLevel);
  }

  private initializeIds(): void {
    this.employeeId =
      this.authService.userProfile?.employeeId ||
      this.authService.userProfile?.email ||
      '';

    this.validateIds();
  }

  private validateIds(): void {
    if (!this.employeeId) {
      this.showError(PEOPLE_METRICS.MESSAGES.EMPLOYEE_AUTH_ERROR);
      return;
    }
    if (!this.vendorId) {
      this.showError(PEOPLE_METRICS.MESSAGES.VENDOR_ID_ERROR);
      return;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSkillsMonthSelect(event: any): void {
    const selectedData = this.processMonthSelection(event, this.skillsMonth);
    if (selectedData) {
      this.selectedSkillsMonth = selectedData.monthValue;
      this.skillsYear = selectedData.year;
      this.loadSkillsData();
    }
  }

  onMetricsMonthSelect(event: any): void {
    const selectedData = this.processMonthSelection(event, this.metricsMonth);
    if (selectedData) {
      this.selectedMetricsMonth = selectedData.monthValue;
      this.metricsYear = selectedData.year;
      this.loadMetricsData();
    }
  }

  private processMonthSelection(
    event: DataObject,
    monthOptions: QuarterOptions[],
  ): { monthValue: number; year: number } | null {
    let selectedValue: number | undefined;
    let year: number = new Date().getFullYear();

    if (typeof event === 'string') {
      const monthOption = monthOptions.find((option) => option.label === event);
      selectedValue = monthOption ? monthOption.value : undefined;
      const yearStr = event.split(' ')[1];
      if (yearStr) {
        year = parseInt(yearStr, 10);
      }
    } else if (event.value) {
      selectedValue = event.value;
      const selectedOption = monthOptions.find(
        (option) => option.value === event.value,
      );
      if (selectedOption) {
        const yearStr = selectedOption.label.split(' ')[1];
        if (yearStr) {
          year = parseInt(yearStr, 10);
        }
      }
    }

    if (
      selectedValue &&
      monthOptions.some((option) => option.value === selectedValue)
    ) {
      return { monthValue: selectedValue, year };
    }

    return null;
  }

  private loadSkillsData(): void {
    this.isLoading = true;
    const quarter = ChartHelper.getQuarter(this.selectedSkillsMonth);

    this.peopleMetricsService
      .getSkillsDistribution(quarter, this.skillsYear.toString())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (skills: SkillsResponse) => {
          if (
            skills.status === PEOPLE_METRICS.MESSAGES.API_SUCCESS_STATUS &&
            skills.data
          ) {
            this.updateChartData(skills.data);
            this.isDataLoaded = true;
          } else {
            this.showError(ErrorMessages.NoDataAvailable);
            this.isDataLoaded = false;
          }
        },
        error: () => {
          this.showError(ErrorMessages.NoDataAvailable);
          this.isDataLoaded = false;
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  private loadMetricsData(): void {
    if (
      this.fieldVisibility !== this.personaType.OpsPersona &&
      !this.employeeId
    ) {
      this.showError(PEOPLE_METRICS.MESSAGES.MISSING_IDS_ERROR);
      return;
    }

    if (!this.vendorId) {
      this.showError(PEOPLE_METRICS.MESSAGES.MISSING_IDS_ERROR);
      return;
    }

    this.isLoading = true;

    const selectedQuarterObj = this.metricsMonth?.find(
      (option) => option.value === this.selectedMetricsMonth,
    );
    const quarter = selectedQuarterObj ? selectedQuarterObj.quarter : '';

    const metricsRequest$ =
      this.fieldVisibility === this.personaType.OpsPersona
        ? this.peopleMetricsService.getPeopleMetrics(
            this.vendorId,
            quarter,
            this.metricsYear.toString(),
          )
        : this.peopleMetricsService.getPeopleMetrics(
            this.vendorId,
            quarter,
            this.metricsYear.toString(),
            this.employeeId,
          );

    metricsRequest$.pipe(untilDestroyed(this)).subscribe({
      next: (metrics: MetricsResponse) => {
        if (
          metrics.status === PEOPLE_METRICS.MESSAGES.API_SUCCESS_STATUS &&
          metrics.data
        ) {
          this.metrics = [
            ...this.createFirstRowMetrics(metrics.data),
            ...this.createSecondRowMetrics(metrics.data),
          ];
        } else {
          this.showError(ErrorMessages.NoDataAvailable);
        }
      },
      error: () => {
        this.showError(ErrorMessages.NoDataAvailable);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  private createFirstRowMetrics(data: any): MetricItem[] {
    return [
      {
        label: PEOPLE_METRICS.LABELS.POSITIONS,
        value: data.totalNumberOfOpenPositions || 0,
      },
      {
        label: PEOPLE_METRICS.LABELS.CLOSURE_SPEED,
        value: data.averageSpeedOfClosure || 0,
      },
      {
        label: PEOPLE_METRICS.LABELS.VOLUNTARY_ATTRITION,
        value: data.countOfVoluntaryAttrition || 0,
      },
      {
        label: PEOPLE_METRICS.LABELS.INVOLUNTARY_ATTRITION,
        value: data.countOfInvoluntaryAttrition || 0,
      },
    ];
  }

  private createSecondRowMetrics(data: any): MetricItem[] {
    return [
      {
        label: PEOPLE_METRICS.LABELS.NICHE_SKILLS,
        value: data.nicheSkillsCandidatesCount || 0,
      },
      {
        label: PEOPLE_METRICS.LABELS.BUFFER_RESOURCES,
        value: data.bufferResourcesCount || 0,
      },
      {
        label: PEOPLE_METRICS.LABELS.CTH,
        value: data.numberOfFullTimeConversions || 0,
      },
    ];
  }
  private updateChartData(skillDistribution: SkillDistribution[]): void {
    if (!skillDistribution?.length) {
      this.showError(ErrorMessages.NoDataAvailable);
      return;
    }

    let colors: string[];
    try {
      colors = ChartHelper.getChartColors();
      if (!colors || colors.length === 0) {
        colors = PEOPLE_METRICS.CHART.DEFAULT_COLORS;
      }
    } catch (error) {
      colors = PEOPLE_METRICS.CHART.DEFAULT_COLORS;
    }

    const topSkills = this.getTopSkills(skillDistribution);

    this.chartData = {
      labels: topSkills.map((skill) => skill.skillName),
      datasets: [
        {
          data: topSkills.map((skill) => skill.employeeCount),
          backgroundColor: colors.slice(0, topSkills.length),
          hoverBackgroundColor: colors.slice(0, topSkills.length),
          borderWidth: 0,
        },
      ],
    };

    this.chartOptions = { ...this.chartOptions };
  }

  private getTopSkills(skills: SkillDistribution[]): SkillDistribution[] {
    return skills
      .sort((a, b) => b.employeeCount - a.employeeCount)
      .slice(0, PEOPLE_METRICS.CHART.TOP_SKILLS_LIMIT);
  }

  private updateChartOptionsForDevice(): void {
    const { isMobile, isSmallMobile } = ChartHelper.getDeviceType();

    const padding = isSmallMobile
      ? PEOPLE_METRICS.CHART.PADDING.SMALL_MOBILE
      : isMobile
        ? PEOPLE_METRICS.CHART.PADDING.MOBILE
        : PEOPLE_METRICS.CHART.PADDING.DESKTOP;

    const fontSize = isSmallMobile
      ? PEOPLE_METRICS.CHART.FONT_SIZE.SMALL_MOBILE
      : isMobile
        ? PEOPLE_METRICS.CHART.FONT_SIZE.MOBILE
        : PEOPLE_METRICS.CHART.FONT_SIZE.DESKTOP;

    const labelOffset = isSmallMobile
      ? PEOPLE_METRICS.CHART.LABEL_OFFSET.SMALL_MOBILE
      : isMobile
        ? PEOPLE_METRICS.CHART.LABEL_OFFSET.MOBILE
        : PEOPLE_METRICS.CHART.LABEL_OFFSET.DESKTOP;

    this.chartOptions.layout.padding = {
      top: padding,
      bottom: padding,
      left: padding,
      right: padding,
    };
    this.chartOptions.plugins.datalabels.font.size = fontSize;
    this.chartOptions.plugins.datalabels.offset = labelOffset;
    this.chartOptions.radius = isSmallMobile
      ? PEOPLE_METRICS.CHART.RADIUS.SMALL_MOBILE
      : isMobile
        ? PEOPLE_METRICS.CHART.RADIUS.MOBILE
        : PEOPLE_METRICS.CHART.RADIUS.DESKTOP;

    this.chartOptions.cutout = isSmallMobile
      ? PEOPLE_METRICS.CHART.CUTOUT.SMALL_MOBILE
      : isMobile
        ? PEOPLE_METRICS.CHART.CUTOUT.MOBILE
        : PEOPLE_METRICS.CHART.CUTOUT.DESKTOP;

    this.chartOptions = { ...this.chartOptions };
  }

  private showError(message: string): void {
    this.toasterService.showError(message, ToastTitle.ERROR);
  }
}
